// app_client/src/components/systemsettings/LocationSettings.jsx
import React, { useEffect, useState } from 'react';
import {
  Anchor,
  Building2,
  Check,
  ChevronRight,
  Edit2,
  Loader2,
  MapPinned,
  Plus,
  Save,
  Trash2,
  X
} from 'lucide-react';
import useLocationStore from '../../store/useLocationStore';
import { useConfirmation } from '../providers/ConfirmationProvider';

const emptyEditState = {
  type: null,
  item: null,
  form: {}
};

const LocationSettings = () => {
  const { confirm } = useConfirmation();
  const { 
    countries, cities, districts, 
    fetchCountries, fetchCities, fetchDistricts,
    addCountry, addCity, addDistrict,
    updateLocation, deleteLocation,
    loading 
  } = useLocationStore();

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [showAdd, setShowAdd] = useState({ type: null, value: '' });
  const [editState, setEditState] = useState(emptyEditState);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setSelectedCity(null);
    fetchCities(country._id);
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    fetchDistricts(city._id);
  };

  const handleSave = async (type) => {
    const value = showAdd.value.trim();
    if (!value) return;
    
    if (type === 'country') {
      await addCountry({ name: value, code: value.substring(0, 2).toUpperCase() });
    } else if (type === 'city' && selectedCountry?._id) {
      await addCity(selectedCountry._id, { name: value });
    } else if (type === 'district' && selectedCity?._id) {
      await addDistrict(selectedCity._id, { name: value });
    }

    setShowAdd({ type: null, value: '' });
  };

  const openEditModal = (type, item) => {
    const baseForm = {
      name: item.name || ''
    };

    const formByType = {
      country: {
        ...baseForm,
        code: item.code || '',
        phoneCode: item.phoneCode || '',
        currency: item.currency || 'TRY',
        isActive: item.isActive !== false
      },
      city: {
        ...baseForm,
        plateCode: item.plateCode || ''
      },
      district: {
        ...baseForm,
        zipCode: item.zipCode || '',
        isCustomsPoint: Boolean(item.isCustomsPoint)
      }
    };

    setEditState({
      type,
      item,
      form: formByType[type] || baseForm
    });
  };

  const closeEditModal = () => {
    setEditState(emptyEditState);
  };

  const updateEditForm = (field, value) => {
    setEditState((current) => ({
      ...current,
      form: {
        ...current.form,
        [field]: value
      }
    }));
  };

  const handleUpdate = async () => {
    if (!editState.item?._id || !editState.form.name?.trim()) return;

    const result = await updateLocation(editState.item._id, editState.form);

    if (result.success) {
      if (result.type === 'country' && selectedCountry?._id === result.data._id) {
        setSelectedCountry(result.data);
      }

      if (result.type === 'city' && selectedCity?._id === result.data._id) {
        setSelectedCity(result.data);
      }

      closeEditModal();
    }
  };

  const handleDelete = async (type, item) => {
    if (await confirm(`${item.name} kaydını silmek istediğinize emin misiniz?`)) {
      const result = await deleteLocation(item._id);
      if (!result.success) return;

      if (type === 'country' && selectedCountry?._id === item._id) {
        setSelectedCountry(null);
        setSelectedCity(null);
        useLocationStore.setState({ cities: [], districts: [] });
      }

      if (type === 'city' && selectedCity?._id === item._id) {
        setSelectedCity(null);
        useLocationStore.setState({ districts: [] });
      }
    }
  };

  const renderActions = (type, item, isActive = false) => (
    <div className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          openEditModal(type, item);
        }}
        className={`p-1.5 rounded-lg transition-colors ${
          isActive ? 'text-white/80 hover:bg-white/10 hover:text-white' : 'text-blue-500 hover:bg-blue-50'
        }`}
        title="Düzenle"
      >
        <Edit2 size={14} />
      </button>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          handleDelete(type, item);
        }}
        className={`p-1.5 rounded-lg transition-colors ${
          isActive ? 'text-white/80 hover:bg-white/10 hover:text-white' : 'text-rose-500 hover:bg-rose-50'
        }`}
        title="Sil"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );

  const editTitle = {
    country: 'Ülke Düzenle',
    city: 'Şehir Düzenle',
    district: 'Bölge Düzenle'
  }[editState.type];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
      <header>
        <h3 className="text-xl font-black text-slate-900 tracking-tight">Lokasyon Hiyerarşisi</h3>
        <p className="text-sm text-slate-500 font-medium">Operasyonel noktaları ve bölgeleri buradan yönetebilirsiniz.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
        <div className="bg-white rounded-3xl p-5 border border-slate-200 shadow-sm space-y-4">
          <div className="flex justify-between items-center px-1">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
              Ülkeler {loading.countries && <Loader2 size={12} className="animate-spin" />}
            </span>
            <button onClick={() => setShowAdd({ type: 'country', value: '' })} className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors">
              <Plus size={16}/>
            </button>
          </div>

          <div className="space-y-1.5 max-h-125 overflow-y-auto pr-1 custom-scrollbar">
            {showAdd.type === 'country' && (
              <div className="p-2 bg-blue-50 rounded-xl flex gap-2 animate-in zoom-in-95">
                <input autoFocus className="flex-1 bg-transparent border-none outline-none text-sm font-bold px-2" placeholder="Ülke Adı..." value={showAdd.value} onChange={e => setShowAdd({...showAdd, value: e.target.value})} />
                <button onClick={() => handleSave('country')} className="bg-blue-600 text-white p-1.5 rounded-lg"><Check size={14}/></button>
              </div>
            )}

            {countries.map(c => {
              const isActive = selectedCountry?._id === c._id;

              return (
                <div 
                  key={c._id}
                  className={`w-full flex items-center justify-between p-2 rounded-2xl font-bold text-sm transition-all group ${isActive ? 'bg-slate-900 text-white shadow-xl shadow-slate-200' : 'bg-slate-50 text-slate-600 hover:bg-white hover:shadow-md border border-transparent hover:border-slate-100'}`}
                >
                  <button type="button" onClick={() => handleCountrySelect(c)} className="min-w-0 flex flex-1 items-center justify-between gap-3 p-1.5 text-left">
                    <span className="min-w-0 flex items-center gap-3">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-md ${isActive ? 'bg-slate-700' : 'bg-slate-200 text-slate-500'}`}>{c.code}</span>
                      <span className="truncate">{c.name}</span>
                    </span>
                    <ChevronRight size={14} className={`shrink-0 transition-transform ${isActive ? 'translate-x-1' : 'opacity-0 group-hover:opacity-100'}`} />
                  </button>
                  {renderActions('country', c, isActive)}
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 border border-slate-200 shadow-sm space-y-4">
          <div className="flex justify-between items-center px-1">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
              Şehirler {loading.cities && <Loader2 size={12} className="animate-spin" />}
            </span>
            {selectedCountry && <button onClick={() => setShowAdd({ type: 'city', value: '' })} className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"><Plus size={16}/></button>}
          </div>

          <div className="space-y-1.5 max-h-125 overflow-y-auto pr-1 custom-scrollbar">
            {!selectedCountry ? (
              <div className="flex flex-col items-center justify-center py-20 text-slate-300">
                <MapPinned size={32} className="mb-2 opacity-20" />
                <p className="text-[10px] font-bold uppercase tracking-widest">Ülke Seçiniz</p>
              </div>
            ) : (
              <>
                {showAdd.type === 'city' && (
                  <div className="p-2 bg-blue-50 rounded-xl flex gap-2 animate-in zoom-in-95 mb-2">
                    <input autoFocus className="flex-1 bg-transparent border-none outline-none text-sm font-bold px-2" placeholder="Şehir Adı..." value={showAdd.value} onChange={e => setShowAdd({...showAdd, value: e.target.value})} />
                    <button onClick={() => handleSave('city')} className="bg-blue-600 text-white p-1.5 rounded-lg"><Check size={14}/></button>
                  </div>
                )}
                {cities.length === 0 && !loading.cities && <p className="text-center py-10 text-[10px] text-slate-400 font-bold uppercase">Kayıt Bulunamadı</p>}
                {cities.map(city => {
                  const isActive = selectedCity?._id === city._id;

                  return (
                    <div
                      key={city._id}
                      className={`w-full flex items-center justify-between p-2 rounded-2xl font-bold text-sm transition-all group ${isActive ? 'bg-blue-600 text-white shadow-xl shadow-blue-100' : 'bg-slate-50 text-slate-600 hover:bg-white hover:shadow-md border border-transparent hover:border-slate-100'}`}
                    >
                      <button type="button" onClick={() => handleCitySelect(city)} className="min-w-0 flex flex-1 items-center justify-between gap-3 p-1.5 text-left">
                        <span className="min-w-0 flex items-center gap-3">
                          <Building2 size={14} className={isActive ? 'text-blue-200' : 'text-slate-400'} />
                          <span className="truncate">{city.name}</span>
                        </span>
                        <ChevronRight size={14} className={`shrink-0 transition-transform ${isActive ? 'translate-x-1' : 'opacity-0 group-hover:opacity-100'}`} />
                      </button>
                      {renderActions('city', city, isActive)}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 border border-slate-200 shadow-sm space-y-4">
          <div className="flex justify-between items-center px-1">
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
              Bölgeler {loading.districts && <Loader2 size={12} className="animate-spin" />}
            </span>
            {selectedCity && <button onClick={() => setShowAdd({ type: 'district', value: '' })} className="p-1.5 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"><Plus size={16}/></button>}
          </div>

          <div className="space-y-1.5 max-h-125 overflow-y-auto pr-1 custom-scrollbar">
            {!selectedCity ? (
              <div className="flex flex-col items-center justify-center py-20 text-slate-300">
                <Anchor size={32} className="mb-2 opacity-20" />
                <p className="text-[10px] font-bold uppercase tracking-widest">Şehir Seçiniz</p>
              </div>
            ) : (
              <>
                {showAdd.type === 'district' && (
                  <div className="p-2 bg-blue-50 rounded-xl flex gap-2 animate-in zoom-in-95 mb-2">
                    <input autoFocus className="flex-1 bg-transparent border-none outline-none text-sm font-bold px-2" placeholder="Bölge/İlçe Adı..." value={showAdd.value} onChange={e => setShowAdd({...showAdd, value: e.target.value})} />
                    <button onClick={() => handleSave('district')} className="bg-blue-600 text-white p-1.5 rounded-lg"><Check size={14}/></button>
                  </div>
                )}
                {districts.length === 0 && !loading.districts && <p className="text-center py-10 text-[10px] text-slate-400 font-bold uppercase">Kayıt Bulunamadı</p>}
                {districts.map(d => (
                  <div 
                    key={d._id}
                    className="w-full flex items-center justify-between p-3.5 rounded-2xl font-bold text-sm bg-slate-50 text-slate-600 border border-transparent group hover:bg-white hover:border-slate-100 hover:shadow-sm transition-all"
                  >
                    <div className="min-w-0 flex items-center gap-3">
                      <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${d.isCustomsPoint ? 'bg-amber-500' : 'bg-slate-300'}`} />
                      <span className="truncate">{d.name}</span>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {d.isCustomsPoint && <span className="text-[9px] bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-md font-black uppercase">Gümrük</span>}
                      {renderActions('district', d)}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      {editState.type && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-blue-600">Lokasyon Yönetimi</p>
                <h3 className="mt-1 text-xl font-black text-slate-900">{editTitle}</h3>
              </div>
              <button type="button" onClick={closeEditModal} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4">
              <label className="block">
                <span className="text-xs font-black uppercase tracking-widest text-slate-400">Ad</span>
                <input
                  value={editState.form.name || ''}
                  onChange={(event) => updateEditForm('name', event.target.value)}
                  className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                />
              </label>

              {editState.type === 'country' && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <label className="block">
                    <span className="text-xs font-black uppercase tracking-widest text-slate-400">Kod</span>
                    <input
                      value={editState.form.code || ''}
                      onChange={(event) => updateEditForm('code', event.target.value.toUpperCase())}
                      className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-black uppercase tracking-widest text-slate-400">Telefon Kodu</span>
                    <input
                      value={editState.form.phoneCode || ''}
                      onChange={(event) => updateEditForm('phoneCode', event.target.value)}
                      className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-black uppercase tracking-widest text-slate-400">Para Birimi</span>
                    <input
                      value={editState.form.currency || ''}
                      onChange={(event) => updateEditForm('currency', event.target.value.toUpperCase())}
                      className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    />
                  </label>
                </div>
              )}

              {editState.type === 'city' && (
                <label className="block">
                  <span className="text-xs font-black uppercase tracking-widest text-slate-400">Plaka Kodu</span>
                  <input
                    value={editState.form.plateCode || ''}
                    onChange={(event) => updateEditForm('plateCode', event.target.value)}
                    className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  />
                </label>
              )}

              {editState.type === 'district' && (
                <>
                  <label className="block">
                    <span className="text-xs font-black uppercase tracking-widest text-slate-400">Posta Kodu</span>
                    <input
                      value={editState.form.zipCode || ''}
                      onChange={(event) => updateEditForm('zipCode', event.target.value)}
                      className="mt-1 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm font-bold outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    />
                  </label>
                  <label className="flex items-center justify-between rounded-2xl border border-slate-200 px-4 py-3">
                    <span>
                      <span className="block text-sm font-black text-slate-700">Gümrük Noktası</span>
                      <span className="block text-xs font-medium text-slate-400">Operasyon ekranlarında özel işaretlenir.</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={Boolean(editState.form.isCustomsPoint)}
                      onChange={(event) => updateEditForm('isCustomsPoint', event.target.checked)}
                      className="h-5 w-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </label>
                </>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={closeEditModal} className="rounded-xl px-5 py-2.5 text-sm font-black text-slate-500 hover:bg-slate-100">
                Vazgeç
              </button>
              <button
                type="button"
                onClick={handleUpdate}
                disabled={loading.action}
                className="flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-black text-white shadow-lg shadow-blue-200 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading.action ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSettings;
